import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppScreen, MysteryType, ChapletType, RosarySession, MoodType, NovenaType, ActiveNovena, PrayerType } from './types';
import { useSubscription } from './contexts/SubscriptionContext';
import { useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { OnboardingProvider, useOnboarding } from './contexts/OnboardingContext';
import { SearchResult } from './constants/searchIndex';
import { logger } from './utils/logger';

// Screens and Modals
import { PrayerSelectionScreen } from './components/screens/PrayerSelectionScreen';
import { PrayerHistoryScreen } from './components/screens/PrayerHistoryScreen';
import NovenaTrackingScreen from './components/screens/NovenaTrackingScreen';
import { RosarySelectionModal } from './components/modals/RosarySelectionModal';
import { ChapletSelectionModal } from './components/modals/ChapletSelectionModal';
import { NovenaSelectionModal } from './components/modals/NovenaSelectionModal';
import { NovenaModal } from './components/modals/NovenaModal';
import { PrayerModal } from './components/modals/PrayerModal';
import { PaywallModal } from './components/modals/PaywallModal';
import { TrialWelcomeModal } from './components/modals/TrialWelcomeModal';
import { PrayerInfoModal } from './components/modals/PrayerInfoModal';
import { SettingsModal } from './components/modals/SettingsModal';
import { NotificationsModal } from './components/modals/NotificationsModal';
import { SignInModal } from './components/modals/SignInModal';
import { SearchModal } from './components/modals/SearchModal';
import { PaymentSuccess } from './components/pages/PaymentSuccess';
import { LiturgicalDebugPanel } from './components/debug/LiturgicalDebugPanel';
import { Toast, useToast } from './components/common/Toast';
import { OnboardingFlow } from './components/onboarding/OnboardingFlow';

// Utilities
import {
  loadRosaryStreakData,
  saveRosaryStreakData,
  createRosarySession,
  createChapletSession,
  completeRosarySession
} from './utils/rosaryStreak';
import { saveNovenaData } from './utils/localStorage';
import {
  loadActiveNovenas,
  startNovena,
  completeNovenaDay,
  getNovenaById,
  removeNovena,
  updateNovenaIntention
} from './utils/novenaTracking';
import { getMysteryForDay, getCurrentPhase } from './utils/novenaCalculations';
import { useNovenaState } from './hooks/useNovenaState';
import { useModalManager } from './hooks/useModalManager';
import { useSearch } from './hooks/useSearch';
import { initGA, analytics } from './utils/analytics';
import { initializeNotifications } from './utils/notifications';
import { initializePlatform } from './utils/platform';
import { subscriptionService } from './services/subscriptionService';
import { FIFTY_FOUR_DAY_NOVENA_INFO, NOVENA_INFO, ROSARY_INFO, CHAPLET_INFO } from './constants/prayerInfo';
import { SUBSCRIPTION_ENABLED } from './config/monetization';
import './utils/devHelpers'; // Load development utilities

const MainApp: React.FC = () => {
  // Navigation state
  const [currentScreen, setCurrentScreen] = useState<AppScreen>('selection');
  const [autoOpenNovenaPrayer, setAutoOpenNovenaPrayer] = useState(false); // Flag for auto-opening novena prayer

  // Modal management
  const { openModal, closeModal, isModalOpen } = useModalManager();

  // Search state
  const search = useSearch();

  // Debug paywall state changes
  React.useEffect(() => {
    logger.paywall.stateChange(isModalOpen('paywall'));
  }, [isModalOpen]);

  // Current prayer context
  const [currentPrayerSession, setCurrentPrayerSession] = useState<RosarySession | null>(null);
  const [currentNovena, setCurrentNovena] = useState<{ novenaId: string; novenaType: NovenaType; day: number } | null>(null);

  // Prayer data
  const [rosaryStreakData, setRosaryStreakData] = useState(() => loadRosaryStreakData());
  const [activeNovenas, setActiveNovenas] = useState<ActiveNovena[]>(() => loadActiveNovenas());
  // Remove unused state - we update local subscription directly based on server response
  
  // Auth and Subscription state
  const auth = useAuth();
  const subscription = useSubscription();
  const { hasAccess, hasSeenWelcome, startTrial, markWelcomeSeen } = subscription;

  // Onboarding state
  const onboarding = useOnboarding();
  const { isOnboarding, startOnboarding } = onboarding;

  // Debug subscription state
  React.useEffect(() => {
    logger.subscription.stateChange({
      hasAccess,
      isTrialActive: subscription.isTrialActive,
      isPremiumUser: subscription.isPremiumUser,
      isTrialExpired: subscription.isTrialExpired,
      daysRemaining: subscription.daysRemaining,
      isAuthenticated: auth.isAuthenticated,
      databaseStatus: subscription.databaseStatus
    });
  }, [hasAccess, subscription, auth.isAuthenticated]);
  const novenaState = useNovenaState();
  const { toasts, hideToast } = useToast();

  // Initialize app
  useEffect(() => {
    initGA();
    initializeNotifications();
    initializePlatform(); // Initialize status bar and safe areas for native platforms

    // Show welcome modal if first time user (but not if they're in onboarding or have a trial).
    // Skipped entirely in freemium mode — the trial welcome flow is a subscription artifact.
    if (SUBSCRIPTION_ENABLED && !hasSeenWelcome && !isOnboarding && !subscription.isTrialActive && !subscription.isPremiumUser) {
      openModal('welcome');
    }
  }, [hasSeenWelcome, openModal, isOnboarding, subscription.isTrialActive, subscription.isPremiumUser]);

  // Handle user authentication and registration
  useEffect(() => {
    const handleAuthenticationFlow = async () => {
      // If user is authenticated but not registered in our system, register them
      if (auth.isAuthenticated && auth.user) {
        try {
          // Try to register the Google user (will succeed if not already registered)
          await subscriptionService.registerGoogleUser(auth.user);

          logger.auth.debug('User authenticated and registered', { email: auth.user.email });
        } catch (error) {
          // Handle registration failure properly
          logger.auth.error('Failed to register user', error);
          // Note: Don't block the user experience, but log the error for debugging
          // User can still use the app, but premium features may not work correctly
        }
      }
      // Note: Removed auto-popup signin modal to avoid conflicts with Google One Tap
      // SignInModal is still available for manual triggers (Settings, Premium features, etc.)
    };

    if (!auth.isLoading) {
      handleAuthenticationFlow();
    }
  }, [auth.isAuthenticated, auth.user, auth.isLoading]);

  // Handle onboarding flow - separate from trial start
  useEffect(() => {
    // Start onboarding if user is authenticated and hasn't completed it
    // This ensures onboarding works for:
    // - New users after first login
    // - Existing trial users (via version check reset)
    // - Testing scenarios (not tied to trial button)
    if (auth.isAuthenticated && !auth.isLoading && !onboarding.hasCompletedOnboarding && !isOnboarding) {
      logger.debug('Starting onboarding for authenticated user', {
        hasCompletedOnboarding: onboarding.hasCompletedOnboarding,
        onboardingVersion: onboarding.version
      });
      startOnboarding();
    }
  }, [auth.isAuthenticated, auth.isLoading, onboarding.hasCompletedOnboarding, onboarding.version, isOnboarding, startOnboarding]);

  // Save rosary streak data whenever it changes
  useEffect(() => {
    saveRosaryStreakData(rosaryStreakData);
  }, [rosaryStreakData]);

  // Navigation handlers
  const handleBackToSelection = () => {
    setCurrentScreen('selection');
  };

  const handleStartNovena = () => {
    logger.debug('handleStartNovena - checking access', {
      hasAccess,
      isTrialActive: subscription.isTrialActive,
      isPremiumUser: subscription.isPremiumUser,
      isAuthenticated: auth.isAuthenticated
    });
    if (!hasAccess) {
      logger.paywall.triggered('54-day novena access denied');
      openModal('paywall');
      return;
    }
    logger.debug('Starting 54-day novena - access granted');
    novenaState.startNovena();
    setCurrentScreen('novena');
    analytics.novenaStarted();
  };

  const handleContinueNovena = () => {
    if (!hasAccess) {
      openModal('paywall');
      return;
    }

    // Create a 54-day novena session for prayer history tracking
    const mystery = getMysteryForDay(novenaState.currentDay);
    const session = createRosarySession(
      mystery,
      '54-day-novena',
      novenaState.intention,
      novenaState.currentDay
    );

    setCurrentPrayerSession(session);

    // Update streak data with new session
    setRosaryStreakData(prev => ({
      ...prev,
      sessions: [...prev.sessions, session]
    }));

    setCurrentScreen('novena');
  };

  const handlePrayRosary = () => {
    openModal('rosarySelection');
  };


  const handlePrayChaplet = () => {
    openModal('chapletSelection');
  };

  // Novena handlers
  const handleStartNewNovena = () => {
    openModal('novenaSelection');
  };

  const handleContinueIndividualNovena = (novenaId: string) => {
    const novena = getNovenaById(novenaId);
    if (novena) {
      const nextDay = Math.min(novena.currentDay, 9);
      setCurrentNovena({
        novenaId: novena.id,
        novenaType: novena.type,
        day: nextDay
      });
      openModal('novenaModal');
    }
  };

  const handleNovenaSelection = (novenaType: NovenaType) => {
    const newNovena = startNovena(novenaType);
    setActiveNovenas(prev => [...prev, newNovena]);

    setCurrentNovena({
      novenaId: newNovena.id,
      novenaType: newNovena.type,
      day: 1
    });

    closeModal();
    openModal('novenaModal');
  };

  const handleRemoveNovena = (novenaId: string) => {
    const success = removeNovena(novenaId);
    if (success) {
      setActiveNovenas(prev => prev.filter(n => n.id !== novenaId));
    }
  };

  // Search handlers
  const handleSearchResultSelect = (result: SearchResult) => {
    if (result.type === 'novena') {
      const novenaType = result.id as NovenaType;
      // Check if novena is already active
      const existingNovena = activeNovenas.find(n => n.type === novenaType);
      
      if (existingNovena) {
        // Continue existing novena
        handleContinueIndividualNovena(existingNovena.id);
      } else {
        // Start new novena
        const newNovena = startNovena(novenaType);
        setActiveNovenas(prev => [...prev, newNovena]);

        setCurrentNovena({
          novenaId: newNovena.id,
          novenaType: newNovena.type,
          day: 1
        });

        openModal('novenaModal');
      }
    } else if (result.type === 'chaplet') {
      // Handle chaplet selection
      handleChapletSelection(result.id as ChapletType);
    }
  };

  // Unified prayer completion function - single source of truth for all prayer history
  const handleUnifiedPrayerComplete = (
    prayerType: PrayerType,
    journalData?: {
      intention?: string;
      reflection?: string;
      mood?: MoodType;
      gratitudes?: string[];
      insights?: string;
      tags?: string[];
    },
    additionalData?: {
      novenaId?: string;
      novenaDay?: number;
      mystery?: MysteryType;
      sessionId?: string;
    }
  ) => {
    let sessionToComplete: RosarySession | null = null;

    // Handle different prayer types
    switch (prayerType) {
      case 'daily-rosary':
      case 'chaplet':
      case '54-day-novena':
        // These use the currentPrayerSession
        if (currentPrayerSession) {
          const updatedStreakData = completeRosarySession(
            currentPrayerSession.id,
            rosaryStreakData,
            undefined, // duration - could be calculated
            journalData
          );
          setRosaryStreakData(updatedStreakData);
          sessionToComplete = currentPrayerSession;
        }
        break;

      case 'novena':
        // Individual novenas: complete the novena day and create a session
        if (currentNovena) {
          const result = completeNovenaDay(currentNovena.novenaId, currentNovena.day, journalData);
          if (result) {
            setActiveNovenas(prev =>
              prev.map(n => n.id === result.novena.id ? result.novena : n)
            );
            // Refresh rosary streak data to include the new session
            setRosaryStreakData(loadRosaryStreakData());
          }
        }
        break;
    }

    // Handle prayer-specific logic
    if (prayerType === '54-day-novena' && additionalData?.novenaDay) {
      // Mark the novena day as complete
      novenaState.markDayComplete(additionalData.novenaDay);
      analytics.dayCompleted(additionalData.novenaDay, getCurrentPhase(additionalData.novenaDay));
    }

    // Track analytics for mystery-based prayers
    if (sessionToComplete?.mystery) {
      analytics.prayerCompleted(1, sessionToComplete.mystery);
    }

    // General analytics for other prayer types
    if (prayerType === 'novena' && currentNovena && additionalData?.mystery) {
      analytics.prayerCompleted(currentNovena.day, additionalData.mystery);
    }
  };

  const handleNovenaComplete = (journalData?: {
    intention?: string;
    reflection?: string;
    mood?: MoodType;
    gratitudes?: string[];
    insights?: string;
    tags?: string[];
  }) => {
    if (currentNovena) {
      // Use unified prayer completion function
      handleUnifiedPrayerComplete(
        'novena',
        journalData,
        {
          novenaId: currentNovena.novenaId,
          novenaDay: currentNovena.day
        }
      );
    }

    setCurrentNovena(null);
    closeModal();
  };

  // Rosary selection handlers
  const handleRosaryMysterySelection = (mystery: MysteryType) => {
    const session = createRosarySession(mystery, 'daily-rosary');
    setCurrentPrayerSession(session);
    
    // Update streak data with new session
    setRosaryStreakData(prev => ({
      ...prev,
      sessions: [...prev.sessions, session]
    }));
    
    closeModal();
    openModal('prayerModal');
    
    analytics.prayerModalOpened(1, mystery);
  };

  // Chaplet selection handlers
  const handleChapletSelection = (chaplet: ChapletType) => {
    const session = createChapletSession(chaplet);
    setCurrentPrayerSession(session);
    
    // Update streak data with new session
    setRosaryStreakData(prev => ({
      ...prev,
      sessions: [...prev.sessions, session]
    }));
    
    closeModal();
    openModal('prayerModal');
    
    // Track chaplet prayer start
    logger.debug('Started chaplet prayer', { chaplet });
  };

  // Prayer completion handlers
  const handleRosaryPrayerComplete = (journalData?: {
    intention?: string;
    reflection?: string;
    mood?: MoodType;
    gratitudes?: string[];
    insights?: string;
    tags?: string[];
  }) => {
    if (currentPrayerSession) {
      // Use unified prayer completion function
      handleUnifiedPrayerComplete(
        currentPrayerSession.prayerType,
        journalData,
        {
          novenaDay: currentPrayerSession.currentDay,
          mystery: currentPrayerSession.mystery,
          sessionId: currentPrayerSession.id
        }
      );
    }

    closeModal();
    setCurrentPrayerSession(null);
  };


  // Modal handlers
  const handleUpgradeClick = () => {
    openModal('paywall');
  };

  const handlePaywallClose = () => {
    closeModal();
  };

  const handleWelcomeStartTrial = () => {
    logger.subscription.trialStarted();
    startTrial();
    markWelcomeSeen();
    closeModal();
  };

  const handleWelcomeSkip = () => {
    markWelcomeSeen();
    closeModal();
  };

  const handleClosePrayerModal = () => {
    closeModal();
    setCurrentPrayerSession(null);
  };

  // Info modal handlers
  const handleShow54DayNovenaInfo = () => {
    openModal('54DayNovenaInfo');
  };

  const handleShowNovenaInfo = () => {
    openModal('novenaInfo');
  };

  const handleShowRosaryInfo = () => {
    openModal('rosaryInfo');
  };

  const handleShowChapletInfo = () => {
    openModal('chapletInfo');
  };

  const handleShowSettings = () => {
    openModal('settings');
  };

  const handleShowHistory = () => {
    setCurrentScreen('history');
  };

  const handleStartRecommendedNovena = (novenaType: string, targetDate: string) => {
    // Create novena with specific target date context
    const newNovena = startNovena(novenaType as any); // Type assertion needed for now
    setActiveNovenas(prev => [...prev, newNovena]);

    setCurrentNovena({
      novenaId: newNovena.id,
      novenaType: newNovena.type,
      day: 1
    });

    openModal('novenaModal');
    logger.debug('Started recommended novena', { novenaType, targetDate });
  };

  const handleStart54DayFromRecommendation = (targetDate: string) => {
    // Start 54-day novena with target date
    novenaState.startNovena();
    setCurrentScreen('novena');
    analytics.novenaStarted();
    logger.debug('Started 54-day novena', { targetDate });
  };

  const handleStart54DayFromOnboarding = (intention: string) => {
    // Directly save novena data to localStorage so NovenaTrackingScreen can load it
    const startDate = new Date();
    const saved = saveNovenaData({
      completedDays: [], // No days completed yet
      startDate: startDate.toISOString(),
      intention
    });

    if (!saved) {
      logger.error('Failed to save novena data to localStorage');
    }

    logger.debug('Started novena with intention', {
      intention,
      startDate: startDate.toISOString(),
      saved
    });

    // Navigate to novena screen with auto-open flag
    // NovenaTrackingScreen will handle opening its own prayer modal and marking day complete
    setAutoOpenNovenaPrayer(true);
    setCurrentScreen('novena');

    analytics.novenaStarted();
    logger.debug('Navigating to novena screen after onboarding');
  };

  const handleStartRosaryFromOnboarding = (mystery: MysteryType) => {
    // Create rosary session with selected mystery
    const session = createRosarySession(mystery, 'daily-rosary');
    setCurrentPrayerSession(session);

    // Update streak data with new session
    setRosaryStreakData(prev => ({
      ...prev,
      sessions: [...prev.sessions, session]
    }));

    // Open prayer modal to start praying
    openModal('prayerModal');
    logger.debug('Started rosary from onboarding', { mystery });
  };

  const handleStartNovenaFromOnboarding = (novenaId: string) => {
    // Map the novenaId to a NovenaType
    // For now, we'll use the novenaId as the novena type
    const newNovena = startNovena(novenaId as NovenaType);
    setActiveNovenas(prev => [...prev, newNovena]);

    setCurrentNovena({
      novenaId: newNovena.id,
      novenaType: newNovena.type,
      day: 1
    });

    // Open novena modal to start first day prayer
    openModal('novenaModal');
    logger.debug('Started novena from onboarding', { novenaId });
  };


  const handleShowDebugPanel = () => {
    openModal('debugPanel');
  };

  const handleShowNotifications = () => {
    openModal('notifications');
  };


  return (
    <ThemeProvider>
      {/* Show onboarding flow if user is onboarding */}
      {isOnboarding && (
        <OnboardingFlow
          onStart54DayNovena={handleStart54DayFromOnboarding}
          onStartRosary={handleStartRosaryFromOnboarding}
          onStartNovena={handleStartNovenaFromOnboarding}
        />
      )}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 safe-area-container">

        {/* Main Screen Content */}
        {currentScreen === 'selection' && (
          <PrayerSelectionScreen
            novenaState={novenaState}
            rosaryStreak={rosaryStreakData}
            activeNovenas={activeNovenas}
            onStartNovena={handleStartNovena}
            onContinueNovena={handleContinueNovena}
            onPrayRosary={handlePrayRosary}
            onPrayChaplet={handlePrayChaplet}
            onSelectChaplet={handleChapletSelection}
            onContinueIndividualNovena={handleContinueIndividualNovena}
            onStartNewNovena={handleStartNewNovena}
            onRemoveNovena={handleRemoveNovena}
            hasAccess={hasAccess}
            onUpgradeClick={handleUpgradeClick}
            onShow54DayNovenaInfo={handleShow54DayNovenaInfo}
            onShowNovenaInfo={handleShowNovenaInfo}
            onShowRosaryInfo={handleShowRosaryInfo}
            onShowChapletInfo={handleShowChapletInfo}
            onShowSettings={handleShowSettings}
            onShowHistory={handleShowHistory}
            onOpenSearch={search.openSearch}
            onStartRecommendedNovena={handleStartRecommendedNovena}
            onStart54DayFromRecommendation={handleStart54DayFromRecommendation}
          />
        )}

        {currentScreen === 'novena' && (
          <NovenaTrackingScreen
            onBackToSelection={handleBackToSelection}
            onUpgradeClick={handleUpgradeClick}
            onShowSettings={handleShowSettings}
            autoOpenPrayer={autoOpenNovenaPrayer}
            onPrayerComplete={(journalData) => {
              // NovenaTrackingScreen handles marking day complete internally
              // We just need to save to unified prayer history
              handleUnifiedPrayerComplete(
                '54-day-novena',
                journalData,
                {
                  novenaDay: novenaState.currentDay,
                  mystery: getMysteryForDay(novenaState.currentDay)
                }
              );

              // Reset auto-open flag after first prayer
              if (autoOpenNovenaPrayer) {
                setAutoOpenNovenaPrayer(false);
              }
            }}
          />
        )}

        {currentScreen === 'history' && (
          <PrayerHistoryScreen
            onBack={handleBackToSelection}
            rosaryStreakData={rosaryStreakData}
          />
        )}

        {/* Modals */}
        <RosarySelectionModal
          isOpen={isModalOpen('rosarySelection')}
          onClose={closeModal}
          onSelectMystery={handleRosaryMysterySelection}
        />

        <ChapletSelectionModal
          isOpen={isModalOpen('chapletSelection')}
          onClose={closeModal}
          onSelectChaplet={handleChapletSelection}
        />

        <NovenaSelectionModal
          isOpen={isModalOpen('novenaSelection')}
          onClose={closeModal}
          onSelectNovena={handleNovenaSelection}
        />

        {currentNovena && (
          <NovenaModal
            isOpen={isModalOpen('novenaModal')}
            novenaType={currentNovena.novenaType}
            novenaId={currentNovena.novenaId}
            currentDay={currentNovena.day}
            existingIntention={activeNovenas.find(n => n.id === currentNovena.novenaId)?.intention}
            onClose={() => {
              closeModal();
              setCurrentNovena(null);
            }}
            onComplete={handleNovenaComplete}
            onIntentionUpdate={(intention: string) => {
              updateNovenaIntention(currentNovena.novenaId, intention);
              setActiveNovenas(prev =>
                prev.map(n => n.id === currentNovena.novenaId ? { ...n, intention } : n)
              );
            }}
          />
        )}

        <PrayerModal
          isOpen={isModalOpen('prayerModal')}
          prayerType={currentPrayerSession?.prayerType || 'daily-rosary'}
          mystery={currentPrayerSession?.mystery}
          chaplet={currentPrayerSession?.chaplet}
          intention={currentPrayerSession?.intention}
          onClose={handleClosePrayerModal}
          onComplete={handleRosaryPrayerComplete}
        />

        <PaywallModal
          isOpen={isModalOpen('paywall')}
          onClose={handlePaywallClose}
          feature="full rosary companion features"
        />

        <TrialWelcomeModal
          isOpen={isModalOpen('welcome')}
          onStartTrial={handleWelcomeStartTrial}
          onSkip={handleWelcomeSkip}
        />

        {/* Prayer Info Modals */}
        <PrayerInfoModal
          isOpen={isModalOpen('54DayNovenaInfo')}
          onClose={closeModal}
          prayerInfo={FIFTY_FOUR_DAY_NOVENA_INFO}
        />

        <PrayerInfoModal
          isOpen={isModalOpen('novenaInfo')}
          onClose={closeModal}
          prayerInfo={NOVENA_INFO}
        />

        <PrayerInfoModal
          isOpen={isModalOpen('rosaryInfo')}
          onClose={closeModal}
          prayerInfo={ROSARY_INFO}
        />

        <PrayerInfoModal
          isOpen={isModalOpen('chapletInfo')}
          onClose={closeModal}
          prayerInfo={CHAPLET_INFO}
        />

        <SettingsModal
          isOpen={isModalOpen('settings')}
          onClose={closeModal}
          onUpgradeClick={handleUpgradeClick}
          onShowDebugPanel={handleShowDebugPanel}
          onShowNotifications={handleShowNotifications}
        />

        <NotificationsModal
          isOpen={isModalOpen('notifications')}
          onClose={closeModal}
        />

        <LiturgicalDebugPanel
          isOpen={isModalOpen('debugPanel')}
          onClose={closeModal}
          onStartNovena={handleStartRecommendedNovena}
          onStart54DayNovena={handleStart54DayFromRecommendation}
        />

        <SignInModal
          isOpen={isModalOpen('signIn')}
          onClose={closeModal}
          title="Sign in to your account"
          message="Sign in to access your prayer history and sync across devices."
        />

        <SearchModal
          isOpen={search.isOpen}
          query={search.query}
          results={search.results}
          onQueryChange={search.setQuery}
          onClose={search.closeSearch}
          onSelectResult={handleSearchResultSelect}
        />

        {/* Toast Notifications */}
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            isVisible={true}
            onClose={() => hideToast(toast.id)}
          />
        ))}

      </div>
    </ThemeProvider>
  );
};

// Router wrapper for the app
const App: React.FC = () => {
  return (
    <Router>
      <OnboardingProvider>
        <Routes>
          <Route path="/success" element={
            <ThemeProvider>
              <PaymentSuccess />
            </ThemeProvider>
          } />
          <Route path="/*" element={<MainApp />} />
        </Routes>
      </OnboardingProvider>
    </Router>
  );
};

export default App;